/**
 * ConditionMeter — pure-presentational 4-segment signal meter (DASH-R1 / #785).
 *
 * Four increasing-height segments (signal-strength shape). Segments `1..tier`
 * are filled with the `colorToken` ramp color; the rest carry the bark unfilled
 * token. The fill derives DIRECTLY from the `colorToken` prop — the component is
 * tier-source-agnostic and does no internal data fetching, no scoring, and no
 * re-bucketing (AC#5: imports no `@/services`/`@/stores`; type-only
 * `@/types/conditions` is permitted). The caller supplies the pre-derived
 * `tier` (the test imports `scoreToLevel` to prove R1 does not re-bucket — AC#4;
 * the component itself imports no scorer — AC#5; these reconcile by keeping the
 * derivation in the caller, never here).
 *
 * Ember-free (RULE-376, decision #2): rating surfaces never use ember/accent.
 */

interface ConditionMeterProps {
  /** Filled-segment count, 1–4 (caller-derived from the rating tier). */
  tier: 1 | 2 | 3 | 4;
  /** Ramp color token for filled segments, e.g. `--color-rating-prime`. */
  colorToken: string;
}

// STATIC geometry. Four increasing-height segments (signal-strength shape),
// ~16–20px tall at the tallest. Unfilled segments use the bark token.
const SEGMENT_HEIGHTS_PX = [8, 12, 16, 20] as const;
const SEGMENT_WIDTH_PX = 5;
const SEGMENT_RADIUS_PX = 1.5;
const UNFILLED_TOKEN = '--color-bark-700';

export function ConditionMeter({ tier, colorToken }: ConditionMeterProps) {
  return (
    <div
      data-testid="condition-meter"
      data-rating-token={colorToken}
      data-rating-tier={tier}
      className="flex items-end gap-[3px]"
      aria-hidden="true"
    >
      {SEGMENT_HEIGHTS_PX.map((heightPx, i) => {
        const isLit = i < tier;
        // Fill derives directly from the colorToken prop (the data-rating-token
        // attribute is an assertion echo only — never a second fill path).
        const fillToken = isLit ? colorToken : UNFILLED_TOKEN;
        return (
          <div
            key={i}
            data-lit={isLit ? 'true' : 'false'}
            style={{
              backgroundColor: `var(${fillToken})`,
              width: `${SEGMENT_WIDTH_PX}px`,
              height: `${heightPx}px`,
              borderRadius: `${SEGMENT_RADIUS_PX}px`,
            }}
          />
        );
      })}
    </div>
  );
}
